import {getSystemTimezone} from '@hconnect/common/utils'
import {DateRange, TimeZone} from '@hconnect/uikit/src/common'
import {timeRangeParamToDateRange} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {SelectionItem} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/types'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {TFunction} from 'i18next'
import moment, {Moment} from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useDowntimePlantId} from '../providers'

import {useTimeRangeParam} from './useTimeRangeParam'

type QuickSelectionId =
  | 'today'
  | 'yesterday'
  | 'last7days'
  | 'currentMonth'
  | 'lastMonth'
  | 'yearToMonthYTM'
  | 'last3months'
  | 'yearToDateYTD'
  | 'lastYear'

export const getQuickSelectionItems = (
  t: TFunction,
  timezone: TimeZone,
  now = new Date()
): SelectionItem<QuickSelectionId>[] => {
  const startOfToday: Moment = moment.utc(now).tz(timezone).startOf('day')
  const endOfToday: Moment = moment.utc(now).tz(timezone).startOf('minute')

  const startOfYesterday: Moment = startOfToday.clone().subtract(1, 'day')
  const endOfYesterday: Moment = startOfYesterday.clone().endOf('day')

  const currentMonth = startOfToday.clone().startOf('month').month()

  const quickSelectItems: SelectionItem<QuickSelectionId>[] = [
    {
      id: 'today',
      title: t('quickSelection.today'),
      dates: [startOfToday.toDate(), endOfToday.toDate()]
    },
    {
      id: 'yesterday',
      title: t('quickSelection.yesterday'),
      dates: [startOfYesterday.toDate(), endOfYesterday.toDate()]
    },
    {
      id: 'last7days',
      title: t('quickSelection.last7days'),
      dates: [startOfToday.clone().subtract(7, 'day').toDate(), endOfYesterday.toDate()]
    },
    {
      id: 'currentMonth',
      title: t('quickSelection.currentMonth'),
      dates: [startOfToday.clone().startOf('month').toDate(), endOfToday.toDate()]
    },
    {
      id: 'lastMonth',
      title: t('quickSelection.lastMonth'),
      dates: [
        startOfToday.clone().subtract(1, 'month').startOf('month').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    }
  ]

  if (currentMonth !== 0) {
    quickSelectItems.push({
      id: 'yearToMonthYTM',
      title: t('quickSelection.yearToMonthYTM'),
      dates: [
        startOfToday.clone().startOf('year').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    })
  }

  quickSelectItems.push(
    {
      id: 'last3months',
      title: t('quickSelection.last3months'),
      dates: [
        startOfToday.clone().subtract(3, 'month').startOf('month').toDate(),
        startOfToday.clone().subtract(1, 'month').endOf('month').toDate()
      ]
    },
    {
      id: 'yearToDateYTD',
      title: t('quickSelection.yearToDateYTD'),
      dates: [startOfToday.clone().startOf('year').toDate(), endOfToday.toDate()]
    },
    {
      id: 'lastYear',
      title: t('quickSelection.lastYear'),
      dates: [
        startOfToday.clone().subtract(1, 'year').startOf('year').toDate(),
        startOfToday.clone().subtract(1, 'year').endOf('year').toDate()
      ]
    }
  )

  return quickSelectItems
}

type UseTimeRangeOptions = {
  now?: Date
  timezone?: string
}

type GenerateTimeRangeParams = {
  t: TFunction
  timezone: string
  now: Date
  timeRange: string
}

export const generateTimeRange = ({t, timezone, now, timeRange}: GenerateTimeRangeParams) => {
  const selectionItems = getQuickSelectionItems(t, timezone, now)
  const [start, end, quickSelectionId] = timeRangeParamToDateRange({timeRange, selectionItems})

  return {from: start ?? now, to: end ?? now, quickSelectionId}
}

export const usePlantTimeRange = (options?: {now: Date}): DateRange<Date> => {
  const {now = new Date()} = options ?? {}
  const plantId = useDowntimePlantId()
  const timezone = useUpmPlantTimezone(plantId)

  return useTimeRange({now, timezone})
}

export const useTimeRange = (options: UseTimeRangeOptions = {}): DateRange<Date> => {
  const plantId = useDowntimePlantId()
  const plantTimezone = useUpmPlantTimezone(plantId)
  const {now = new Date(), timezone: explicitTimezone} = options
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [timeRangeOrSlot] = useTimeRangeParam()
  const timezone = explicitTimezone ?? plantTimezone ?? getSystemTimezone()

  return generateTimeRange({
    t,
    timezone,
    now,
    timeRange: timeRangeOrSlot
  })
}
